import { FormDialogContextType, useFormDialogContext } from '@react-admin/ra-form-layout';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  Button,
  DataProvider,
  useDataProvider,
  useNotify,
  useRedirect,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { FieldValues, UseFormReturn, useFormContext } from 'react-hook-form';
import { match } from 'ts-pattern';

import { Button as ButtonWithPermissions } from '@sh4res/bo-common/components';
import type {
  ExtraValidationResult,
  ResourceDraft,
  StepperFormButtonProps,
  StepperFormProps,
  StepperFormStep,
  ValidateFunc,
} from '@sh4res/bo-common/components/StepperForm';
import {
  ResourceBasePath,
  useCreatePromise,
  useResourceBasePath,
  useUpdatePromise,
} from '@sh4res/bo-common/hooks';
import { getErrorMessage } from '@sh4res/bo-common/utils';
import { buildLink } from '@sh4res/bo-common/utils/build_link';
import { nestedForEach } from '@sh4res/bo-common/utils/nested_for_each';
import { translateBackendError } from '@sh4res/bo-common/utils/translate_error';

export const validateForm = async (
  form: UseFormReturn<FieldValues>,
  notify: ReturnType<typeof useNotify>,
  dataProvider: DataProvider,
  resourceBasePath: ResourceBasePath,
  validate?: ValidateFunc,
): Promise<boolean> => {
  const isFormValid = await form.trigger();

  if (!isFormValid) {
    const values = form.getValues();
    nestedForEach(values, (keys, value) => {
      form.setValue(keys.join('.'), value, { shouldTouch: true });
    });
    notify('ra.message.invalid_form', { type: 'error' });
    return false;
  }

  if (validate) {
    const extraValidation: ExtraValidationResult = await Promise.resolve(
      validate(form, dataProvider, resourceBasePath),
    );
    if (!extraValidation.isValid) {
      if (extraValidation.message) {
        notify(extraValidation.message, { type: 'error' });
      }
      return false;
    }
  }

  return true;
};

export type SubmitGuard = {
  isSubmitting: boolean;
  /** Runs `fn` unless another guarded submission is in flight (then resolves to undefined). */
  runExclusive: <T>(fn: () => Promise<T>) => Promise<T | undefined>;
};

/**
 * Prevents concurrent submissions. A ref is used for the lock because state updates are async,
 * so rapid clicks could otherwise slip through before the button is re-rendered as disabled.
 */
export const useSubmitGuard = (): SubmitGuard => {
  const inFlightRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const runExclusive = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    if (inFlightRef.current) {
      return undefined;
    }
    inFlightRef.current = true;
    setIsSubmitting(true);
    try {
      return await fn();
    } finally {
      inFlightRef.current = false;
      setIsSubmitting(false);
    }
  }, []);

  return useMemo(() => ({ isSubmitting, runExclusive }), [isSubmitting, runExclusive]);
};

export const BackButton = ({
  setActiveStep,
  step,
  onBack,
  isLoading,
  'data-testid': dataTestId,
}: StepperFormButtonProps) => {
  const formContext = useFormContext();
  const { prevStep } = step;

  if (prevStep === null) {
    return null;
  }

  return (
    <Button
      onClick={() => {
        onBack?.(step.index, formContext);
        formContext.clearErrors();
        setActiveStep(prevStep);
      }}
      variant="text"
      color="secondary"
      size="medium"
      disabled={isLoading}
      disableRipple
      data-testid={dataTestId}
      label="resources.bo_common.actions.back"
    />
  );
};

export const NextButton = ({
  step,
  isLoading,
  onClick,
  'data-testid': dataTestId,
}: Pick<StepperFormButtonProps, 'step' | 'isLoading'> & {
  onClick: () => void | Promise<void>;
  'data-testid'?: string;
}) => {
  const { nextStep, nextDisabled } = step;
  if (nextStep === null) {
    return null;
  }

  return (
    <Button
      disableRipple
      disabled={isLoading || nextDisabled}
      variant="contained"
      size="medium"
      id="next_button"
      data-testid={dataTestId}
      onClick={onClick}
      label="resources.bo_common.actions.next"
    />
  );
};

export const SaveDraftButton = ({
  draft,
  permissions,
  customSave,
  validate,
  isLoading,
  draftDisabled,
  submitGuard,
  'data-testid': dataTestId,
}: Pick<StepperFormProps, 'permissions' | 'draft'> &
  Pick<StepperFormStep, 'customSave' | 'validate'> & {
    isLoading: boolean;
    draftDisabled?: boolean;
    submitGuard: SubmitGuard;
    'data-testid'?: string;
  }) => {
  const form = useFormContext();
  const [saveDraft] = useCreatePromise();
  const [updateDraft] = useUpdatePromise();
  const formDialogContext = useFormDialogContext() as FormDialogContextType | null;
  const refresh = useRefresh();
  const redirect = useRedirect();
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const resourceBasePath = useResourceBasePath();

  const handleError = useCallback(
    (draft: ResourceDraft, errorMessage: string) => {
      if (customSave?.onError) {
        customSave.onError(errorMessage);
      } else {
        const errorTranslated = translateBackendError(
          {
            resource: `resources.bo_common.actions.save_draft.errors.${draft.resource.replace('b2b/', '')}`,
            error: errorMessage,
          },
          (translation) => translate(translation),
        );
        notify('resources.bo_common.actions.save_draft.failure', {
          type: 'warning',
          messageArgs: { error: errorTranslated },
          multiLine: true,
        });
      }
    },
    [translate, notify, customSave],
  );

  const { isSubmitting, runExclusive } = submitGuard;

  const onSaveDraft = useCallback(
    async () =>
      runExclusive(async () => {
        if (!draft) {
          return;
        }
        try {
          let data = await draft.transform(form.getValues());
          if (customSave) {
            data = customSave.transform(data);
            const isFormValid = await validateForm(
              form,
              notify,
              dataProvider,
              resourceBasePath,
              validate,
            );
            if (!isFormValid) {
              return;
            }
          }
          const { id } = await match(draft)
            .with({ action: 'create' }, async (draftData) =>
              saveDraft(draftData.resource, { data }),
            )
            .with({ action: 'update' }, async (draftData) =>
              updateDraft(draftData.resource, {
                id: draftData.resourceId,
                data,
              }),
            )
            .exhaustive();
          refresh();
          formDialogContext?.close();
          if (customSave?.onSuccess) {
            await customSave.onSuccess(id, data);
          } else {
            notify('resources.bo_common.actions.save_draft.success', { type: 'success' });
            if (draft.action === 'create') {
              redirect(
                buildLink(draft.resource, 'show', id, {
                  tab: 'details',
                }),
              );
            }
          }
        } catch (error) {
          handleError(draft, getErrorMessage(error));
        }
      }),
    [
      runExclusive,
      draft,
      notify,
      redirect,
      customSave,
      handleError,
      formDialogContext,
      form,
      refresh,
      saveDraft,
      updateDraft,
      validate,
      dataProvider,
      resourceBasePath,
    ],
  );
  if (!draft) {
    return;
  }

  return (
    <ButtonWithPermissions
      color="primary"
      permissions={permissions}
      disabled={isLoading || isSubmitting || draftDisabled}
      onClick={onSaveDraft}
      label={customSave?.label ?? 'resources.bo_common.actions.continue_later'}
      data-testid={dataTestId}
    />
  );
};
